import { Role, RoleType } from "@the-real-insight/tri-model";

import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useAtom } from "jotai";
import { authenticationAtom } from '../State';
import { useCustomerTheme } from "../Theme";
import {
    Banknote,
    CircleUser,
    Settings,
    ShieldCheck,
    User,
    UserCheck,
    UserCog,
    UserPlus,
} from "lucide-react";

import { cn } from "@/lib/utils";

interface Properties {
    className?: string;
    role: Role;
    showIssuer?: boolean;
    showDescription?: boolean;
    customerName?: string;
}

export default function RoleLabel(properties: Properties) {
    const { t } = useTranslation();
    const { theme } = useCustomerTheme();
    const [_authentication] = useAtom(authenticationAtom) as any;
    const [isHovered, setIsHovered] = useState(false);

    // Add null check for role
    if (!properties.role || !properties.role.type) {
        return <div className={properties.className || ''}>
            <div className="flex items-center gap-1 p-1">
                <div className="text-sm text-muted-foreground">
                    {t('settingsPanel.userManagementPanel.invalidRole')}
                </div>
            </div>
        </div>;
    }

    const getIconInfo = (): { Icon: any; color: string } => {
        const roleId = properties.role.getRoleID();
        if (roleId === 'customerAdministrator') return { Icon: ShieldCheck, color: theme.primary };
        if (roleId === 'billingAdministrator') return { Icon: Banknote, color: theme.secondary };
        if (roleId === 'technicalAdministrator') return { Icon: Settings, color: theme.tertiary };
        switch (properties.role.type) {
            case RoleType.role: return { Icon: User, color: theme.primary };
            case RoleType.parameterizableRole: return { Icon: UserCog, color: theme.secondary };
            case RoleType.parameterizedRole: return { Icon: UserCheck, color: theme.tertiary };
            case RoleType.partnerDelegation: return { Icon: UserPlus, color: theme.primaryDark };
            default: return { Icon: CircleUser, color: theme.primary };
        }
    };

    const icon = getIconInfo();

    const roleId = properties.role.getRoleID();
    const nameKey = `settingsPanel.userManagementPanel.roleName_${roleId}`;
    const translatedName = t(nameKey);
    const roleName = translatedName !== nameKey ? translatedName : properties.role.getRoleName();
    const descriptionKey = `settingsPanel.userManagementPanel.roleDescription_${roleId}`;
    const translatedDescription = t(descriptionKey, { customerName: properties.customerName });
    const description = translatedDescription !== descriptionKey ? translatedDescription : (properties.role.description || '');

    return <div
        className={cn(
            "cursor-grab rounded-lg px-2 py-1.5 transition-all",
            isHovered && "translate-x-0.5 bg-muted",
            properties.className,
        )}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
    >
        <div className="flex items-start gap-2.5">
            {/* Die Rollenfarbe kommt aus dem Kunden-Theme und bleibt deshalb
                ein Inline-Wert (inkl. Alpha-Anteil für die Kachel). */}
            <div
                className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-lg transition-colors"
                style={{ backgroundColor: icon.color + (isHovered ? '22' : '12') }}
            >
                <icon.Icon aria-hidden="true" className="h-3.5 w-3.5" style={{ color: icon.color }} />
            </div>
            <div className="min-w-0">
                <div className="font-bold">{roleName}</div>
                {properties.role.getParameterName && properties.role.getParameterName() && (
                    <div className="mt-px text-xs leading-tight text-muted-foreground">
                        {properties.role.getParameterName()}
                    </div>
                )}
                {properties.showDescription && description && (
                    <div className="mt-0.5 text-sm leading-snug text-muted-foreground">
                        {description}
                    </div>
                )}
            </div>
        </div>
    </div>;
}
